import { UploadedFile } from '@omnea-app/fe-requests/uploadedFile'
import { FileStorageApiClient } from '@omnea-app/packages/universal/fileStorageApiClient/fileStorageApiClient'

export type UploadFilesResult = {
  uploaded: Required<UploadedFile>[]
  /** Indices into the original `files` array for S3 PUTs that failed */
  failedFileIndices: Set<number>
  /** The rejection reasons for failed S3 PUTs, preserving per-file error detail */
  failedReasons: unknown[]
}

export const uploadFilesToS3 = async (files: File[], authorization: string): Promise<UploadFilesResult> => {
  const fileStorageApiClient = new FileStorageApiClient({ authorization })
  const { data: uploadResponse, error } = await fileStorageApiClient.POST('/upload', {
    body: {
      documents: files.map((doc, index) => ({
        name: doc.name,
        type: doc.type,
        uploadId: index,
      })),
    },
  })

  if (!uploadResponse) {
    throw new Error('Presigned URL fetch failed', { cause: error })
  }

  const putResults = await Promise.allSettled(
    uploadResponse.map(async (doc) => {
      const content = files[doc.uploadId]
      if (!content) throw new Error('Document content not found')
      const response = await fetch(doc.presignedPutUrl, { method: 'PUT', body: content })
      if (!response.ok) {
        const errorBody = await response.text().catch(() => response.statusText)
        throw new Error(`S3 upload failed ${response.status}: ${errorBody}`)
      }
    })
  )

  // Index results by the original input position so `uploaded` preserves drop order
  // (the API response may return documents in a different order to the request).
  const resultByUploadId = new Map<number, { doc: (typeof uploadResponse)[number]; result: PromiseSettledResult<void> }>()
  putResults.forEach((result, i) => {
    const doc = uploadResponse[i]
    if (doc) resultByUploadId.set(doc.uploadId, { doc, result })
  })

  const uploaded: Required<UploadedFile>[] = []
  const failedFileIndices = new Set<number>()
  const failedReasons: unknown[] = []

  files.forEach((file, i) => {
    const entry = resultByUploadId.get(i)
    if (!entry) return
    if (entry.result.status === 'fulfilled') {
      uploaded.push({ key: entry.doc.filestoreId, filename: file.name, contentType: file.type })
    } else {
      failedFileIndices.add(i)
      failedReasons.push(entry.result.reason)
    }
  })

  return { uploaded, failedFileIndices, failedReasons }
}
